import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../../hooks/useAuth';
import { useFirebase } from '../../../hooks/useFirebase';
import { queryDelete, queryGet, queryPost, queryPut } from '../../tools/tools';

const ENDPOINT = '/coupons';
const KEY = 'coupons';

export const useCouponEntityQuery = () => {
  const { user } = useAuth();
  const { remoteConfig } = useFirebase();
  const queryClient = useQueryClient();

  const [isMutating, setIsMutating] = useState(false);
  const [mutationError, setMutationError] = useState(null);

  const queryKey = [KEY, user?.uid];

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey,
    queryFn: () => queryGet(remoteConfig, ENDPOINT, user?.accessToken),
    enabled: !!user?.accessToken && !!remoteConfig
  });

  const wrapMutate = (mutationFn) => ({ data: payload, onSuccess, onError, onComplete }) => {
    setIsMutating(true);
    setMutationError(null);
    mutationFn(payload, {
      onSuccess: async (res) => {
        onSuccess?.(res);
        onComplete?.(null, res);
        await refetch();
      },
      onError: (err) => {
        setMutationError(err);
        onError?.(err);
        onComplete?.(err, null);
      },
      onSettled: () => setIsMutating(false)
    });
  };

  // EntityTable passes raw form data — normalise it before sending
  const normalise = (raw) => ({
    ...raw,
    code: raw.code?.toUpperCase().trim(),
    discountValue: raw.discountValue !== '' && raw.discountValue != null ? parseFloat(raw.discountValue) : null,
    usageLimit: raw.usageLimit !== '' && raw.usageLimit != null ? parseInt(raw.usageLimit) : null,
    expiresAt: raw.expiresAt
      ? raw.expiresAt.includes('T') ? raw.expiresAt : `${raw.expiresAt}T23:59:59`
      : null,
    consultantId: raw.consultantId || null,
    active: raw.active ?? true
  });

  const { mutate: _create, isPending: isCreating, reset: resetCreate } = useMutation({
    mutationFn: (item) => queryPost(remoteConfig, ENDPOINT, normalise(item), user?.accessToken)
  });

  const { mutate: _update, isPending: isUpdating, reset: resetUpdate } = useMutation({
    mutationFn: (item) => queryPut(remoteConfig, ENDPOINT, normalise(item), user?.accessToken)
  });

  const { mutate: _remove, isPending: isDeleting, reset: resetRemove } = useMutation({
    mutationFn: (id) => queryDelete(remoteConfig, ENDPOINT, id, user?.accessToken)
  });

  const reset = () => { resetCreate(); resetUpdate(); resetRemove(); };

  const getById = async (id, onSuccess) => {
    try {
      setIsMutating(true);
      const result = await queryGet(remoteConfig, `${ENDPOINT}/${id}`, user?.accessToken);
      queryClient.setQueryData(queryKey, (old) => {
        if (!old) return [result];
        const idx = old.findIndex((e) => e.id === result.id);
        if (idx >= 0) { const u = [...old]; u[idx] = result; return u; }
        return [...old, result];
      });
      const display = {
        ...result,
        expiresAt: result.expiresAt ? result.expiresAt.substring(0, 10) : null
      };
      onSuccess?.(display);
      return display;
    } catch (err) {
      setMutationError(err);
      throw err;
    } finally {
      setIsMutating(false);
    }
  };

  return {
    data: data ?? [],
    loading: isLoading,
    error: isError,
    create: wrapMutate(_create),
    update: wrapMutate(_update),
    remove: wrapMutate(_remove),
    getById,
    reset,
    refetch,
    mutating: isCreating || isUpdating || isDeleting || isMutating,
    mutatingError: mutationError
  };
};
